import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import Loader from '@atoms/Atom/Loader';
import { useRouter } from 'next/router';

const OfficeContext = createContext();

export const useOffice = () => {
  const context = useContext(OfficeContext);
  if (!context) {
    throw new Error('useOffice must be used within an OfficeProvider');
  }
  return context;
};

export default function OfficeLoader({ children }) {
  const [officeReady, setOfficeReady] = useState(false);
  const [officeError, setOfficeError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isContentAddIn, setIsContentAddIn] = useState(false);
  const [activeView, setActiveView] = useState(null);

  const router = useRouter();
  const initializationComplete = useRef(false);
  const originalPushState = useRef(null);
  const originalReplaceState = useRef(null);
  const hasRestoredRoute = useRef(false);
  const isProcessingRedirect = useRef(false);
  const lastRedirectRoute = useRef(null);

  const isOfficeIframeHost = () => {
    const isIframe = window.self !== window.top;
    const isOfficeUserAgent = /Office|Word|Excel|PowerPoint/i.test(navigator.userAgent);
    const hasOfficeOrigin = document.referrer.includes('officeapps.live.com') || document.referrer.includes('office365.com');

    return isIframe && (isOfficeUserAgent || hasOfficeOrigin);
  };

  useEffect(() => {
    if (typeof window === 'undefined' || initializationComplete.current) return;

    if (!isOfficeIframeHost()) {
      setIsLoading(false);
      initializationComplete.current = true;
      return;
    }

    const initializeOffice = async () => {
      try {
        // Store original history methods
        originalPushState.current = window.history.pushState?.bind(window.history);
        originalReplaceState.current = window.history.replaceState?.bind(window.history);

        // Temporarily patch history methods during load
        window.history.pushState = (...args) => {
          console.log('History pushState intercepted during Office load');
          originalPushState.current?.(...args);
        };

        window.history.replaceState = (...args) => {
          console.log('History replaceState intercepted during Office load');
          originalReplaceState.current?.(...args);
        };

        if (!window.Office) {
          await loadOfficeJs();
        }

        await new Promise((resolve, reject) => {
          if (window.Office.context) {
            resolve();
          } else {
            window.Office.onReady(resolve);
            setTimeout(() => reject(new Error('Office.js initialization timeout')), 10000);
          }
        });

        const host = window.Office?.context?.host;
        const supportedHosts = [Office.HostType.PowerPoint];

        if (host && supportedHosts.includes(host)) {
          setIsContentAddIn(true);
          setupDocumentHandlers();
        }

        setOfficeReady(true);
      } catch (error) {
        console.error('Office initialization error:', error);
        setOfficeError(error);
      } finally {
        if (originalPushState.current) {
          window.history.pushState = originalPushState.current;
        }
        if (originalReplaceState.current) {
          window.history.replaceState = originalReplaceState.current;
        }
        setIsLoading(false);
        initializationComplete.current = true;
      }
    };

    initializeOffice();

    return () => {
      if (originalPushState.current) {
        window.history.pushState = originalPushState.current;
      }
      if (originalReplaceState.current) {
        window.history.replaceState = originalReplaceState.current;
      }
    };
  }, []);

  const loadOfficeJs = () => {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://appsforoffice.microsoft.com/lib/1/hosted/Office.js';
      script.async = true;
      script.onload = resolve;
      script.onerror = () => reject(new Error('Failed to load Office.js'));
      document.head.appendChild(script);
    });
  };

  const setupDocumentHandlers = () => {
    if (!window.Office?.context?.document) {
      console.warn('Document API not available in current host');
      return;
    }

    // ****** initial view state ******
    safeOfficeCall(
      () => window.Office.context.document.getActiveViewAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          handleViewChange(result.value);
        }
      }),
      'getActiveViewAsync'
    );

    // ****** view change handler ******
    safeOfficeCall(
      () => window.Office.context.document.addHandlerAsync(
        Office.EventType.ActiveViewChanged,
        (eventArgs) => handleViewChange(eventArgs.activeView)
      ),
      'addHandlerAsync'
    );
  };

  const handleViewChange = (newView) => {
    setActiveView(newView);

    if (newView === Office.ActiveView.Edit) {
      safeOfficeCall(() => {
        const settings = window.Office.context.document.settings;
        const lastRoute = settings.get('lastRoute');

        if (lastRoute && !isProcessingRedirect.current) {
          // Only redirect if:
          // 1. We have a saved route
          // 2. We're not already processing a redirect
          // 3. The route is different from current
          // 4. It's not the same as our last redirect attempt
          if (lastRoute !== router.asPath && lastRoute !== lastRedirectRoute.current) {
            console.log('Redirecting to saved edit route:', lastRoute);
            isProcessingRedirect.current = true;
            lastRedirectRoute.current = lastRoute;

            setTimeout(() => {
              window.location.href = lastRoute;
            }, 100);
          }
        } else if (!lastRoute) {
          // No saved route yet - save current route
          saveCurrentRoute();
        }
      }, 'handleEditMode');
    } else if (newView === Office.ActiveView.Read && !hasRestoredRoute.current) {
      // Reset redirect flags when entering Read mode
      isProcessingRedirect.current = false;
      lastRedirectRoute.current = null;
      restoreLastRoute();
      hasRestoredRoute.current = true;
    }
  };

  const saveCurrentRoute = () => {
    try {
      if (!isContentAddIn || !window.Office?.context?.document?.settings) {
        return;
      }

      // Reset redirect tracking when saving a new route
      isProcessingRedirect.current = false;
      lastRedirectRoute.current = null;

      const settings = window.Office.context.document.settings;
      settings.set('lastRoute', router.asPath);
      settings.saveAsync((result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          console.error('Failed to save settings:', result.error);
        } else {
          console.log('Edit route saved:', router.asPath);
        }
      });
    } catch (error) {
      console.error('Error saving current route:', error);
    }
  };

  const restoreLastRoute = () => {
    try {
      if (hasRestoredRoute.current) return;

      const settings = window.Office.context.document.settings;
      const lastRoute = settings.get('lastRoute');

      if (lastRoute && lastRoute !== router.asPath) {
        console.log('Restoring full route:', lastRoute);
        hasRestoredRoute.current = true;

        setTimeout(() => {
          if (lastRoute !== window.location.pathname + window.location.search) {
            window.location.href = lastRoute;
          }
        }, 100);
      }
    } catch (error) {
      console.error('Error restoring last route:', error);
    }
  };

  const safeOfficeCall = (callback, operationName = 'unknown') => {
    try {
      if (!window.Office || !window.Office.context) {
        throw new Error('Office.js not available');
      }
      return callback();
    } catch (error) {
      console.error(`Office API error in ${operationName}:`, error);
      setOfficeError({
        code: error.code || 5001,
        message: error.message || `Office API error in ${operationName}`,
        name: error.name || 'Office API Error'
      });
      return null;
    }
  };

  useEffect(() => {
    if (officeReady &&
        isContentAddIn &&
        activeView === Office.ActiveView.Edit &&
        !isProcessingRedirect.current) {
      saveCurrentRoute();
    }
  }, [router.pathname, officeReady, isContentAddIn, activeView]);

  const shouldRender = officeReady || (typeof window !== 'undefined' && !window.Office);

  const contextValue = {
    officeReady,
    officeError,
    isLoading,
    isContentAddIn,
    activeView
  };

  return (
    <OfficeContext.Provider value={contextValue}>
      {shouldRender ? children : <Loader />}
    </OfficeContext.Provider>
  );
}
